import { createContext, useContext, type ReactNode } from 'react'
import type { AuthResult, Transport, Uploader } from '@assembly/slack'

export interface PlatformStorage {
  get(key: string): Promise<string | null>
  set(key: string, value: string): Promise<void>
  remove(key: string): Promise<void>
}

/**
 * Everything that differs between the web, macOS and iOS builds. The shared UI
 * talks to Slack and the host OS only through this interface.
 */
export interface Platform {
  kind: 'web' | 'macos' | 'ios'
  /** Sends Slack Web API calls (server proxy, Electron main process, or native HTTP). */
  transport: Transport
  /** Uploads file bytes to a Slack upload URL. */
  uploader: Uploader
  /** Downloads a private Slack file (url_private / thumbnails) with the user's token. */
  fetchFile(url: string, token: string): Promise<Blob>
  storage: PlatformStorage
  /** WebSocket URL of the event relay, or null to always poll. */
  relayUrl: string | null
  /** Assembly server prefs API for syncing sidebar sections across devices; null = this device only. */
  prefsUrl?: string | null
  /** Assembly server AI API ("Organize with AI"); null when there's no server. */
  aiUrl?: string | null
  /** Whether OAuth sign-in is available (needs an Assembly server). */
  canOAuth: boolean
  /** Native app downloads to advertise (web only). `null` = not published yet. */
  downloads?: { mac: string | null; ios: string | null }
  /**
   * Starts "Sign in with Slack". Native platforms resolve with the result once the
   * browser redirects back into the app; the web navigates away and resolves null.
   */
  signIn(): Promise<AuthResult | null>
  /** Picks up an OAuth result delivered to the page via redirect (web only). */
  consumeAuthRedirect?(): AuthResult | { error: string } | null
  openExternal(url: string): void
  notify?(title: string, body: string, onClick?: () => void): void
  setBadge?(count: number): void
  /** Status bar / window chrome style for the current screen: 'dark' = dark background. */
  setChrome?(background: 'dark' | 'light'): void
  /** Called once the UI has rendered, so the shell knows an updated UI bundle works (macOS). */
  ready?(): void
  /** Over-the-air UI updates (macOS): notifies when a new UI is downloaded, and reloads into it. */
  updates?: {
    onReady(callback: () => void): () => void
    apply(): Promise<void>
  }
}

const PlatformContext = createContext<Platform | null>(null)

export function PlatformProvider({ platform, children }: { platform: Platform; children: ReactNode }) {
  return <PlatformContext.Provider value={platform}>{children}</PlatformContext.Provider>
}

export function usePlatform(): Platform {
  const platform = useContext(PlatformContext)
  if (!platform) throw new Error('usePlatform must be used inside <PlatformProvider>')
  return platform
}

/** Browser Notification API helper shared by platforms that run in a web view. */
export function webNotify(title: string, body: string, onClick?: () => void) {
  if (typeof Notification === 'undefined') return
  const show = () => {
    const n = new Notification(title, { body, silent: false })
    n.onclick = () => {
      window.focus()
      onClick?.()
      n.close()
    }
  }
  if (Notification.permission === 'granted') show()
  else if (Notification.permission !== 'denied') Notification.requestPermission().then((p) => p === 'granted' && show())
}
